import React, { useEffect, useState } from 'react';
import { BlobDto } from '../../Repository/eventbookyapi';
import { useAuth } from '../../Contexts/AuthContext';
import { SubscriptionLevel } from '../../Types/SubscriptionLevel';
import { TextService } from '../../Services/TextService';
import LeftCommunicate from '../Communicate/LeftCommunicate';
import LayoutLeftMenu from '../../Components/Layout/LayoutLeftMenu';
import { DocumentService } from '../../Services/ImageService';
import UploadImage, { UploadImageType } from '../../Components/Upload/UploadImage';
import { List, message, Image, Tooltip, Modal } from 'antd';
import { CloseCircleFilled, CloseOutlined, CloseSquareOutlined, CopyOutlined, DeleteFilled } from '@ant-design/icons';
import { formatDateString } from '../../Helpers/DateHelpers';
import { getCurrentLanguage } from '../../Helpers/RouteHelper';
import { getVersionedImageUri } from '../../Components/Select/ImageSelect';

const AdminPictureArchive = () => {
    const [images, setImages] = useState<BlobDto[]>([]);
    const { user } = useAuth();
    const GetText = TextService.GetText;

    useEffect(() => {
        loadImages();
    }, []);

    const savePublicImageFinished = async (uri?: string) => {
        loadImages();
        message.success(GetText("MyEventDetails_Saved"));
    };

    const deleteImage = async (item: BlobDto) => {
        await DocumentService.DeletePublicImage(item.relativeUri!);
        loadImages();
        message.success(GetText("PictureArchive_PictureHasBeenRemoved"));
    };

    const confirmDeleteImage = (item: BlobDto) => {
        Modal.confirm({
            title: GetText("PictureArchive_ConfirmRemove"),
            content: GetText("PictureArchive_ConfirmRemoveContent"),
            okText: GetText("Common_Yes"),
            cancelText: GetText("Common_No"),
            onOk: () => deleteImage(item), // Call the deleteImage function if confirmed
        });
    };

    const loadImages = async () => {
        DocumentService.GetPublicImages().then(images => {
            setImages(images);
        });
    };

    // Calculate total file size
    const getTotalFileSize = () => {
        return images.reduce((total, image) => total + (image.size || 0), 0);
    };

    const canUploadMoreImages = () => {
        const totalSizeMB = getTotalFileSize() / (1024 * 1024); // Convert total size to MB
        return totalSizeMB < 10; // Return true if under the limit
    };

    // An image uploaded with the same file name replaces the old one at the same URL, so the
    // browser would keep showing its cached copy. Display only - the copied link stays clean.
    const getVersionedUri = (item: BlobDto) => {
        return getVersionedImageUri(item.uri!, item.lastModified);
    };

    const copyToClipboard = (text: string) => {
        navigator.clipboard.writeText(text).then(() => {
            message.success("Link copied to clipboard!");
        }).catch(() => {
            message.error("Failed to copy the link.");
        });
    };

    return (
        <LayoutLeftMenu title={GetText("PictureArchive_Title")} menu={<LeftCommunicate selected={"picturearchive"}></LeftCommunicate>}>
            {user?.subscriptionLevel == SubscriptionLevel.Free ? (
                <i>
                    {GetText("Groups_UpgradeTo")} <a href={"/" + getCurrentLanguage() + "/Pricing"}>Premium</a>
                </i>
            ) : (
                <div>
                    <>{GetText("PictureArchive_Description")}</>
                    <br />
                    <br />
                    {!canUploadMoreImages() ? <i>
                        <i>{GetText("PictureArchive_MaxSizeAchived")})</i>
                    </i> : <>{!user?.hasOnlyReadRights && (<UploadImage crop={false} uploadImageType={UploadImageType.Public} uploadFinish={savePublicImageFinished} />)}</>}

                    <List
                        style={{ marginTop: "20px" }}
                        grid={{ gutter: 18 }}
                        dataSource={images}
                        locale={{ emptyText: GetText("PictureArchive_NoImagesInArchive") }} // Change "No data" text

                        renderItem={(item: BlobDto) => (
                            <List.Item>
                                <div
                                    style={{
                                        position: "relative",
                                        border: "1px solid #d9d9d9",
                                        borderRadius: "5px",
                                        width: "220px", // Fixed width for all items
                                        height: "320px", // Fixed height for all items
                                        display: "flex",
                                        flexDirection: "column",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                    }}
                                >
                                    <Image
                                        style={{
                                            width: "220px",
                                            height: "200px",
                                            objectFit: "cover", // Ensures the image fills the container while maintaining aspect ratio
                                            borderRadius: "5px",
                                        }}
                                        preview={true}
                                        src={getVersionedUri(item)}
                                    />
                                    {!user?.hasOnlyReadRights && (
                                        <CloseCircleFilled
                                            alt="Delete image"
                                            style={{
                                                zIndex: "9999",
                                                right: "10px",
                                                top: "10px",
                                                color: "#8B0000", // Dark red color
                                                fontSize: "19px",
                                                position: "absolute",
                                                cursor: "pointer",
                                            }}

                                            onClick={() => confirmDeleteImage(item)}
                                        />)}
                                    <div style={{ marginTop: "10px", textAlign: "center" }}>
                                        <p style={{ margin: 0, fontWeight: "bold" }}>{item.name!.split("/").pop()}</p>
                                        <p style={{ margin: 0, color: "#888" }}>{formatDateString(item.lastModified!)}</p>
                                        <p style={{ margin: 0, color: "#888" }}>{(item.size! / (1024 * 1024)).toFixed(2)} MB</p>
                                        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "5px" }}>
                                            <div onClick={() => copyToClipboard(item.uri!)} style={{ cursor: "pointer", color: "#1890ff" }}>
                                                Kopiera länk
                                            </div>
                                            <Tooltip title="Copy Link">
                                                <CopyOutlined
                                                    style={{ cursor: "pointer", color: "#1890ff" }}
                                                    onClick={() => copyToClipboard(item.uri!)}
                                                />
                                            </Tooltip>
                                        </div>
                                    </div>
                                </div>
                            </List.Item>
                        )}
                    />

                    {/* Display total file size */}
                    <div style={{ marginTop: "20px" }}>
                        {GetText("PictureArchive_TotalSize")} {(getTotalFileSize() / (1024 * 1024)).toFixed(2)} MB / 10 MB
                    </div>
                </div>
            )}
        </LayoutLeftMenu>
    );
};

export default AdminPictureArchive;